import { Group, ScrollArea, Stack, Textarea, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { UseFormReturnType } from '@mantine/form';
import { useState } from 'react';
import type { PreSaveInitiative } from '../../../api/initiative-client/initiative-client.type';
import { RelativeBusinessValueUnits } from '../../../api/initiative-client/initiative-client.type';
import i18n from '../../../base-dictionary';
import { STANDARD_DATE_FORMATS } from '../../../helpers/date-time-helpers';
import { baseWhite, inkLight, redBase, skyDark } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Radio } from '../../../ui-library/radio/radio';
import { Text } from '../../../ui-library/typography/typography';
import { InitiativeDeleteModal } from './initiative-delete-modal';
import { commonInputProps } from './initiative-modal.helpers';
import classes from './initiative-modal.module.css';
import { InitiativeModalMode } from './initiative-modal.type';

type Props = {
  mode: InitiativeModalMode;
  form: UseFormReturnType<Partial<PreSaveInitiative>>;
  onNext: (data: Partial<PreSaveInitiative>) => void;
  onCancel: () => void;
  onDelete?: () => void;
  isDeleting?: boolean;
};

export function StepOne({ mode, form, onNext, onCancel, onDelete, isDeleting = false }: Props) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleDeleteConfirm = async () => {
    await onDelete?.();
    setIsDeleteModalOpen(false);
  };

  return (
    <>
      <form onSubmit={form.onSubmit((values) => onNext(values))}>
        <ScrollArea>
          <Stack>
            <Text>{i18n.t('strategy.initiatives.form.description')}</Text>

            <TextInput
              required
              label={i18n.t('strategy.initiatives.form.name_label')}
              placeholder={i18n.t('strategy.initiatives.form.name_placeholder')}
              maxLength={150}
              {...form.getInputProps('name')}
              {...commonInputProps}
            />

            <Divider />

            <Textarea
              required
              label={i18n.t('strategy.initiatives.form.description_label')}
              placeholder={i18n.t('strategy.initiatives.form.description_placeholder')}
              maxLength={1000}
              maxRows={8}
              {...form.getInputProps('description')}
              {...commonInputProps}
              autosize
              classNames={classes}
            />

            <Divider />

            <Group gap={24}>
              <DatePickerInput
                label={i18n.t('strategy.initiatives.form.start_date_label')}
                placeholder={i18n.t('strategy.initiatives.form.date_placeholder')}
                {...form.getInputProps('start_date')}
                popoverProps={{ zIndex: 600 }}
                {...commonInputProps}
                flex={1}
                rightSection={<Icon name="calendar_month" size={16} color={skyDark} />}
                rightSectionPointerEvents="none"
                h={80}
                valueFormat={STANDARD_DATE_FORMATS.FULL_DATE}
              />
              <DatePickerInput
                label={i18n.t('strategy.initiatives.form.end_date_label')}
                placeholder={i18n.t('strategy.initiatives.form.date_placeholder')}
                {...form.getInputProps('end_date')}
                popoverProps={{ zIndex: 600 }}
                {...commonInputProps}
                flex={1}
                rightSection={<Icon name="calendar_month" size={16} color={skyDark} />}
                rightSectionPointerEvents="none"
                h={80}
                valueFormat={STANDARD_DATE_FORMATS.FULL_DATE}
              />
            </Group>

            <Group gap={24} h={96}>
              <TextInput
                label={i18n.t('strategy.initiatives.form.business_value_label')}
                placeholder={i18n.t('strategy.initiatives.form.business_value_placeholder')}
                {...form.getInputProps('relative_business_value')}
                {...commonInputProps}
                flex={1}
                type="number"
                min={0}
                max={999999999}
                style={{
                  alignSelf: 'flex-start',
                }}
              />
              <Group gap={16} flex={1}>
                <Radio
                  label={i18n.t('strategy.initiatives.form.numeric_label')}
                  checked={form.values.relative_business_value_unit === RelativeBusinessValueUnits.numeric}
                  onChange={() =>
                    form.setFieldValue('relative_business_value_unit', RelativeBusinessValueUnits.numeric)
                  }
                />
                <Radio
                  label={i18n.t('strategy.initiatives.form.dollar_label')}
                  checked={form.values.relative_business_value_unit === RelativeBusinessValueUnits.dollars}
                  onChange={() =>
                    form.setFieldValue('relative_business_value_unit', RelativeBusinessValueUnits.dollars)
                  }
                />
              </Group>
            </Group>
          </Stack>
        </ScrollArea>

        <Stack gap={24}>
          <Divider />

          <Group justify="space-between">
            {mode === InitiativeModalMode.Edit && (
              <Button
                radius="xl"
                variant="outline"
                onClick={() => setIsDeleteModalOpen(true)}
                leftSection={<Icon name="delete" color={inkLight} size={18} pointer />}
                style={{ borderColor: redBase }}
                disabled={isDeleting}
              >
                {i18n.t('strategy.initiatives.form.delete_button')}
              </Button>
            )}
            <Group justify="flex-end" gap={16} flex={1}>
              <Button radius="xl" variant="outline" onClick={onCancel}>
                {i18n.t('strategy.initiatives.form.cancel')}
              </Button>

              <Button
                type="submit"
                radius="xl"
                variant="secondary"
                rightSection={<Icon name="arrow_forward" color={baseWhite} size={16} pointer />}
              >
                {i18n.t('strategy.initiatives.form.next_step')}
              </Button>
            </Group>
          </Group>
        </Stack>
      </form>
      <InitiativeDeleteModal
        opened={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleDeleteConfirm}
        isDeleting={isDeleting}
        initiativeName={form.values.name}
      />
    </>
  );
}
